import SFlex from 'components/opinionless/SFlex'
import SLink from 'components/opinionless/SLink'
import { useRouter } from 'next/router'
import React, { useContext, useEffect, useRef, useState } from 'react'
import { GoogleReCaptcha, GoogleReCaptchaProvider } from 'react-google-recaptcha-v3'
import SButton from '../../../components/opinionless/SButton'
import SText from '../../../components/opinionless/SText'
import { useTranslation } from '../../../i18n'
import { SStyled, useSTheme } from '../../../styles/theme'

import { useGetProgramsMutation } from 'types/graphql'

import ConditionalRender from 'utils/conditionalRender'

import SConfirmModal from 'components/opinionless/SConfirmModal'
import { ga4_useTitle } from 'utils/ga4'
import { LocalStorageKeys } from 'utils/localStorage'
import XBanner from 'v2/components/XBanner'
import SBox from '../../../components/opinionless/SBox'
import { OrganizationContext } from '../../../v2/context/organization'

import { WIZARD_STEPS_MAPPING } from 'containers/Apply/LongBeachWizard'
import { updatePercentageAndStep } from 'v2/components/XProgressBar/utils'

export const Container = SStyled(SBox)`
    width: 100%;
    max-width: 600px;
`
export const LBBox = SStyled(SBox)`
    margin-top: 24px;
`

export const LBText = SStyled(SText)`
    margin-top: 24px;
`

export const LBTextContainer = SStyled(SFlex)``

export const LBButton = SStyled(SButton)`
    margin-top: 24px;
    margin-bottom: 24px;
`
export const LBTextHeader = SStyled(SText)`
    ${(p) => `

        @media screen and (max-width: ${p.theme.breakpoints[0]}) {
            font-size: 24px;
            line-height: 130%;
        }
    `}
`
export const LBTextSubHeader = SStyled(SText)`
    margin-top: 24px;

    ${(p) => `

        @media screen and (max-width: ${p.theme.breakpoints[0]}) {
            font-size: 14px;
            line-height: 130%;
            color: #202020;
            opacity: 87%;
        }
    `}
`
export const LBTextBody = SStyled(SText)`
    margin-top: 24px;

    ${(p) => `

        @media screen and (max-width: ${p.theme.breakpoints[0]}) {
            font-size: 14px;
            
        }
    `}
`

interface Props {
    setCurrentStep?: (number) => void
    fontFamily: any
    updatePercentage: any
}

const ApplicationStepLBIntroduction: React.FC<Props> = (props: Props) => {
    const organizationContext = useContext(OrganizationContext)
    const theme = useSTheme()
    const shouldShowConfirmModal = !localStorage.getItem(LocalStorageKeys.SHOWN_VERIFICATION_POPUP)
    const [isButtonDisabled, setIsButtonDislabed] = useState(true)
    const { t } = useTranslation(window.LANG_NS)
    const router = useRouter()
    const [isExistingProgram, setIsExistingProgram] = useState(false)
    const [getProgram] = useGetProgramsMutation()
    const [confirmModalVisible, setConfirmModalVisible] = useState(false)

    const modalRef = useRef()
    const fontFamily = props.fontFamily
    ga4_useTitle('Introduction')

    const onRecoverClick = () => {
        router.push(`/${organizationContext.program.urlFragment}/apply/recover`)
    }

    const handleModalConfirm = () => {
        setConfirmModalVisible(false)
        localStorage.setItem(LocalStorageKeys.SHOWN_VERIFICATION_POPUP, new Date().toISOString())
        props.setCurrentStep(2)
    }

    useEffect(() => {
        if (!shouldShowConfirmModal) {
            setIsButtonDislabed(false)
        }
    }, [shouldShowConfirmModal])

    useEffect(() => {
        getProgram({
            variables: {
                programUrl: router.query['program-name'] as string,
                includeClosedPeriod: false,
            },
        }).then((res) => {
            if (res.data.getPrograms?.success) {
                setIsExistingProgram(true)
            } else {
                router.push(
                    `${window.PERSON?.applicationDetails[0].program?.urlFragment || window.PROGRAM.urlFragment}/404`
                )
            }
        })
    }, [])

    const { percentage, wizardStep, childWizzardStep } = updatePercentageAndStep(
        WIZARD_STEPS_MAPPING.basicInfo,
        WIZARD_STEPS_MAPPING.basicInfo.introduction,
        WIZARD_STEPS_MAPPING
    )
    props.updatePercentage(percentage, wizardStep, childWizzardStep)

    if (isExistingProgram) {
        return (
            <Container>
                <LBTextHeader
                    sx={{
                        textAlign: 'left',
                        fontSize: '32px',
                        color: theme.colors.secondaryColor,
                        lineHeight: '120%',
                        fontFamily: fontFamily.header,
                        fontWeight: '900',
                    }}
                >
                    {t('application_header')}
                </LBTextHeader>
                <ConditionalRender if={t('application_recover').length > 0}>
                    <div style={{ marginTop: '24px' }}>
                        <XBanner
                            title={t('already_started_application')}
                            description={t('application_continue_left_off')}
                            isButton={true}
                            isCloseButton={true}
                            buttonTitle={t('continue')}
                            buttonAction={() => {
                                onRecoverClick()
                            }}
                            type="info"
                            isLongBeachV2Design
                        ></XBanner>
                    </div>
                </ConditionalRender>
                <ConditionalRender if={t('application_sub_header').length > 0}>
                    <LBTextSubHeader
                        sx={{
                            textAlign: 'left',
                            fontSize: '16px',
                            color: theme.colors.black,
                            fontFamily: 'Raleway',
                            fontWeight: '700',
                        }}
                        dangerouslySetInnerHTML={{ __html: t('application_sub_header') }}
                    ></LBTextSubHeader>
                </ConditionalRender>

                <LBTextContainer>
                    <LBTextBody
                        sx={{
                            whiteSpace: 'pre-line',
                            textAlign: 'left',
                            fontSize: '14px',
                            color: theme.colors.gray400,
                            lineHeight: '140%',
                            fontFamily: fontFamily.body,
                            fontWeight: '400',
                        }}
                        dangerouslySetInnerHTML={{ __html: t('application_body') }}
                    ></LBTextBody>
                </LBTextContainer>

                <ConditionalRender if={shouldShowConfirmModal}>
                    <GoogleReCaptchaProvider reCaptchaKey={process.env.NEXT_PUBLIC_CAPTCHA_KEY}>
                        <GoogleReCaptcha
                            onVerify={() => {
                                setIsButtonDislabed(false)
                            }}
                        />
                    </GoogleReCaptchaProvider>
                </ConditionalRender>
                <div style={{ marginTop: '32px' }}>
                    <XBanner
                        title={t('have_more_questions')}
                        description={t('visit_faq')}
                        isButton={true}
                        isCloseButton={true}
                        buttonTitle={t('learn_more')}
                        buttonAction={() =>
                            window.open(
                                'https://www.commoninja.com/faq/lp/e4eded02-fec6-4dc2-b040-31e31fc9b518',
                                '_blank'
                            )
                        }
                        type="info"
                        isLongBeachV2Design
                    ></XBanner>
                </div>
                <LBButton
                    onClick={() => (shouldShowConfirmModal ? setConfirmModalVisible(true) : props.setCurrentStep(2))}
                    title="Next"
                    sx={{ [isButtonDisabled ? 'bg' : '']: 'gray200' }}
                    disabled={isButtonDisabled}
                    width={'100%'}
                    textStyle={{
                        fontWeight: 700,
                        fontSize: '14px',
                        lineHeight: '140%',
                        fontFamily: fontFamily.body,
                        color: theme.colors.white,
                    }}
                    ga4_key={t('continue', { lang: 'en' })}
                >
                    {t('continue')}
                </LBButton>
                <ConditionalRender if={window.PROGRAM.name === 'Pathway to Income Equity'}>
                    <SLink
                        href="https://first5sonomacounty.org/wp-content/uploads/2021/11/FAQS-GBI-1.pdf"
                        target="_blank"
                        style={{
                            color: theme.colors.secondaryColor,
                            fontWeight: 700,
                            fontSize: '16px',
                            fontFamily: fontFamily.body,
                            lineHeight: '140%',
                        }}
                    >
                        <span>{t('need_help_question')}</span>
                    </SLink>
                </ConditionalRender>
                <SConfirmModal
                    title={t('privacy_policy_title')}
                    message={<SText dangerouslySetInnerHTML={{ __html: t('privacy_policy_content') }} />}
                    confirmMessage={t('i_understand')}
                    visible={confirmModalVisible}
                    onConfirm={handleModalConfirm}
                    modalRef={modalRef}
                    isLongBeachV2Design
                    onClickOutside={() => setConfirmModalVisible(false)}
                />
            </Container>
        )
    } else {
        return null
    }
}

export default ApplicationStepLBIntroduction
